// What the keyboard can reach: the one selector for the focus trap and for the boxes that scroll (reachScroller).
const FOCUSABLE =
  "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE));
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Pauses what a visitor may be watching or listening to. A silent loop behind text (video-backdrop) is decoration and
// keeps playing; a video player (video-player) pauses without being marked as paused by the visitor.
function pauseAllMedia() {
  document.querySelectorAll('video-player').forEach((player) => player.pauseForPage?.());
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (!video.closest('video-backdrop, video-player')) video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/*
  The quantity stepper (snippets/quantity-input.liquid): a number field with a button on each side. A button steps the
  value by the field's step and keeps to its limits; a step that changes the value sends one `change`, as typing does,
  so the surface that owns the field (the product form, the cart) answers one event whatever moved it. The plus button
  at the highest quantity is `aria-disabled` and stays in the tab order, so the keyboard does not lose its place at the
  limit. With a mouse, the click that puts the focus in the field selects the number, ready to be typed over.
*/
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.input.addEventListener('pointerdown', () => (this.focusedByPointer = document.activeElement !== this.input), {
      signal,
    });
    this.input.addEventListener('change', () => this.validateQtyRules(), { signal });
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
    this.validateQtyRules();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.quantityUpdateUnsubscriber?.();
  }

  onClick(event) {
    if (event.target === this.input) {
      if (this.focusedByPointer && fineHover.matches) this.input.select();
      this.focusedByPointer = false;
      return;
    }
    const button = event.target.closest('button[name]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    this.step(button.name === 'plus' ? 1 : -1);
  }

  /**
   * Moves the value one step. From 0, plus lands on the quantity rule's minimum when that is above one step; a minus
   * from the minimum goes to the field's own floor (0 in the cart, which removes the line).
   */
  step(direction) {
    const input = this.input;
    const before = input.value;
    const ruleMin = parseInt(input.dataset.min, 10) || 1;
    if (direction > 0) {
      if (ruleMin > (parseInt(input.step, 10) || 1) && !parseInt(input.value, 10)) {
        input.value = ruleMin;
      } else {
        input.stepUp();
      }
    } else if (input.min !== '' && (parseInt(input.value, 10) || 0) <= ruleMin) {
      input.value = input.min;
    } else {
      input.stepDown();
    }
    if (input.value !== before) input.dispatchEvent(new Event('change', { bubbles: true }));
    this.validateQtyRules();
  }

  /**
   * Marks the plus button when the value has reached the ceiling: the field's `max`, or, where the field is left free
   * because its value is past the ceiling, the `data-max` it was written with. Without either there is no ceiling.
   */
  validateQtyRules() {
    const plus = this.querySelector("button[name='plus']");
    if (!plus) return;
    const ceiling = parseInt(this.input.max || this.input.dataset.max, 10);
    if (parseInt(this.input.value, 10) >= ceiling) plus.setAttribute('aria-disabled', 'true');
    else plus.removeAttribute('aria-disabled');
  }
}

if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/*
 * The theme's motion tokens (assets/interaction.css) as the browser resolves them, for an animation a script has to
 * run itself (a height that CSS cannot transition). With reduced motion the time is 0.01 ms, so the one switch in the
 * stylesheet governs these too. Where the browser cannot resolve the time, nothing animates.
 */
function motionTokens({ long = false } = {}) {
  const style = getComputedStyle(document.documentElement);
  const time = style.getPropertyValue(long ? '--motion-time-long' : '--motion-time').trim();
  const milliseconds = parseFloat(time) * (time.endsWith('ms') ? 1 : 1000);
  return {
    duration: Number.isFinite(milliseconds) ? milliseconds : 0,
    easing: style.getPropertyValue('--motion-ease').trim() || 'linear',
  };
}

/**
 * Draws the cart icon's count again from the rendered cart-icon-bubble section, then sends 'cart-icon:updated' on the
 * document, so a script that animates the count (assets/cart-shake.js) finds the new one.
 */
function drawCartBubble(html) {
  const bubble = document.getElementById('cart-icon-bubble');
  const source = html && bubble && new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
  if (!source) return;
  bubble.innerHTML = source.innerHTML;
  document.dispatchEvent(new CustomEvent('cart-icon:updated'));
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Fetches a section's HTML (the Section Rendering API) and keeps the latest answers: `size` of them (0 keeps none; a
 * request on its way is still shared), each for `life` ms and only if `keep(url)`; `clearOn` is a pubsub event that
 * clears them. `get(url, signal)` gives the text, or rejects with an Error whose `status` is the HTTP status (and
 * `retryAfter`, in milliseconds, when a 429 says how long to wait); the request stops when no caller waits for it.
 * Also `page` (parsed), `peek`, `drop(url)`, `clear()` and `generation`, which counts the clears. Nothing is kept in
 * the theme editor, and a section loaded there clears everything.
 */
function sectionAnswers({ size = 10, life = Infinity, keep = () => true, clearOn } = {}) {
  const kept = new Map();
  const asked = new Map();
  let generation = 0;

  const clear = () => {
    kept.clear();
    asked.clear();
    generation += 1;
  };
  if (clearOn) subscribe(clearOn, clear);
  document.addEventListener('shopify:section:load', clear);

  const peek = (url) => {
    const entry = kept.get(url);
    if (!entry) return null;
    kept.delete(url);
    if (Date.now() - entry.time > life) return null;
    kept.set(url, entry);
    return entry.text;
  };

  const remember = (url, text) => {
    if (size < 1 || window.Shopify?.designMode || !keep(url)) return;
    kept.set(url, { text, time: Date.now() });
    if (kept.size > size) kept.delete(kept.keys().next().value);
  };

  const request = (url) => {
    const controller = new AbortController();
    const born = generation;
    const ask = { controller, waiting: 0 };
    ask.promise = fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          const seconds = response.status === 429 ? Number(response.headers.get('retry-after')) : 0;
          throw Object.assign(new Error(`HTTP ${response.status}`), {
            status: response.status,
            retryAfter: seconds > 0 ? seconds * 1000 : undefined,
          });
        }
        return response.text();
      })
      .then((text) => {
        if (born === generation) remember(url, text);
        return text;
      })
      .finally(() => {
        if (asked.get(url) === ask) asked.delete(url);
      });
    asked.set(url, ask);
    return ask;
  };

  const get = (url, signal) => {
    if (signal?.aborted) return Promise.reject(signal.reason);
    const text = peek(url);
    if (text !== null) return Promise.resolve(text);
    const ask = asked.get(url) ?? request(url);
    ask.waiting += 1;
    return new Promise((resolve, reject) => {
      const leave = () => {
        if ((ask.waiting -= 1) === 0) {
          ask.controller.abort();
          if (asked.get(url) === ask) asked.delete(url);
        }
        reject(signal.reason);
      };
      signal?.addEventListener('abort', leave, { once: true });
      ask.promise.then(resolve, reject).finally(() => signal?.removeEventListener('abort', leave));
    });
  };

  return {
    get,
    peek,
    clear,
    drop: (url) => kept.delete(url),
    page: (url, signal) => get(url, signal).then((text) => new DOMParser().parseFromString(text, 'text/html')),
    get generation() {
      return generation;
    },
  };
}

/*
 * Runs `load(signal)` once `element` is within 500 px of the screen, then drops its aria-busy. A rejection is logged,
 * and tried again `retries` times, a second longer each time, when it was the network or a server error. A try that
 * takes REQUEST_TIMEOUT ends there (its signal aborts with a TimeoutError), is not tried again, and leaves the part in
 * its failed state, so a request that never answers does not keep the part busy. The stop aborts the signal of the try
 * on its way. Returns the stop.
 */
function whenNear(element, load, { retries = 0 } = {}) {
  const controller = new AbortController();
  const { signal } = controller;
  let timer = 0;

  const run = async (attempt) => {
    try {
      await load(AbortSignal.any([signal, AbortSignal.timeout(REQUEST_TIMEOUT)]));
    } catch (error) {
      if (signal.aborted) return;
      if (error.name !== 'TimeoutError' && !(error.status < 500) && attempt < retries) {
        timer = setTimeout(() => run(attempt + 1), 1000 * (attempt + 1));
        return;
      }
      console.warn(`Core: <${element.localName}> could not be loaded`, error);
    }
    if (!signal.aborted) element.removeAttribute('aria-busy');
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      run(0);
    },
    { rootMargin: '0px 0px 500px 0px' }
  );
  observer.observe(element);

  return () => {
    observer.disconnect();
    controller.abort();
    clearTimeout(timer);
  };
}

/*
 * Adds a script or stylesheet unless the page has it, and settles once the file has loaded or failed. Files are the
 * same file whatever their `v` (Shopify's asset_url writes the last change in it), so one is never added twice; one
 * still on its way is waited for. A failed file is removed and forgotten, so the next call asks again. A script that
 * fails rejects; a stylesheet that fails resolves, and is logged.
 */
const assetsOnTheWay = new Map();

function loadAsset(href) {
  const keyOf = (address) => {
    try {
      const url = new URL(address, document.baseURI);
      url.searchParams.delete('v');
      return url.origin + url.pathname + url.search;
    } catch {
      return address;
    }
  };
  const key = keyOf(href);
  if (assetsOnTheWay.has(key)) return assetsOnTheWay.get(key);
  const sheet = key.split('?')[0].endsWith('.css');
  const attribute = sheet ? 'href' : 'src';
  const selector = sheet ? 'link[rel="stylesheet"][href]' : 'script[src]';
  if ([...document.querySelectorAll(selector)].some((element) => keyOf(element.getAttribute(attribute)) === key)) {
    return Promise.resolve();
  }
  const loading = new Promise((resolve, reject) => {
    const element = document.createElement(sheet ? 'link' : 'script');
    if (sheet) element.rel = 'stylesheet';
    element[attribute] = href;
    element.addEventListener('load', resolve, { once: true });
    element.addEventListener(
      'error',
      () => {
        element.remove();
        if (!sheet) return reject(new Error(`${href} did not load`));
        console.warn('Core: a stylesheet did not load', href);
        resolve();
      },
      { once: true }
    );
    document.head.append(element);
  });
  assetsOnTheWay.set(key, loading);
  loading.then(
    () => assetsOnTheWay.delete(key),
    () => assetsOnTheWay.delete(key)
  );
  return loading;
}

// Work that arrives in bursts (scroll, resize, keystrokes), done once in the next frame.
function oncePerFrame(work) {
  let frame = 0;
  return {
    schedule() {
      frame ||= requestAnimationFrame(() => {
        frame = 0;
        work();
      });
    },
    cancel() {
      cancelAnimationFrame(frame);
      frame = 0;
    },
  };
}

/*
 * Keyboard reach of a box that scrolls: it is a tab stop while it overflows and holds no control of its own (`forced`:
 * whatever it holds; `vertical`: by height). With a `label` it is also a region, named by its table's caption or the
 * label. Returns whether the box is a tab stop.
 */
function reachScroller(box, { label = '', vertical = false, forced = false } = {}) {
  const overflows = vertical ? box.scrollHeight - box.clientHeight > 1 : box.scrollWidth - box.clientWidth > 1;
  const stop = overflows && (forced || !box.querySelector(FOCUSABLE));
  if (stop) {
    box.tabIndex = 0;
    if (label) {
      box.setAttribute('role', 'region');
      const caption = box.querySelector('caption');
      if (caption) {
        caption.id ||= `caption-${crypto.randomUUID()}`;
        box.setAttribute('aria-labelledby', caption.id);
      } else {
        box.setAttribute('aria-label', label);
      }
    }
  } else {
    if (box.getAttribute('tabindex') === '0') box.removeAttribute('tabindex');
    if (label) for (const name of ['role', 'aria-label', 'aria-labelledby']) box.removeAttribute(name);
  }
  return stop;
}

// Storage that can be blocked, full or hold anything; nothing here throws. `get`/`set`/`remove` keep text,
// `read`/`write` JSON (`read` with `list` gives [] for anything but an array); `session` picks sessionStorage.
const safeStorage = (() => {
  const area = (session) => (session ? sessionStorage : localStorage);
  const get = (key, { session = false } = {}) => {
    try {
      return area(session).getItem(key);
    } catch {
      return null;
    }
  };
  const set = (key, text, { session = false } = {}) => {
    try {
      area(session).setItem(key, text);
      return true;
    } catch {
      return false;
    }
  };
  const remove = (key, { session = false } = {}) => {
    try {
      area(session).removeItem(key);
    } catch {
      // Nothing to take it out of.
    }
  };
  const read = (key, { list = false, session = false } = {}) => {
    let value = null;
    try {
      value = JSON.parse(get(key, { session }));
    } catch {
      // Not JSON: nothing is stored.
    }
    return list ? (Array.isArray(value) ? value : []) : value;
  };
  const write = (key, value, { session = false } = {}) => set(key, JSON.stringify(value), { session });
  return { get, set, remove, read, write };
})();

// The words and choices the layout writes as attributes of one hidden element (layout/theme.liquid), where the browser
// decodes their entities. `themeStrings(name)` gives one by its dataset name, or '' when the page has none; `from` is
// another element that carries words the same way (the compare tray's).
const themeStrings = (name, from = document.querySelector('[data-theme-strings]')) => from?.dataset[name] ?? '';

// The cart for every script that asks: `read()` gives it whole (null when unreadable), one request for callers at the
// same time. A request that paints the cart takes a `ticket()` and paints only if `claim(ticket)`: no later one has.
const sharedCart = (() => {
  let issued = 0;
  let painted = 0;
  let pending = null;
  return {
    ticket: () => (issued += 1),
    claim(ticket) {
      if (ticket < painted) return false;
      painted = ticket;
      return true;
    },
    read() {
      pending ??= fetch(`${routes.cart_url}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
        .finally(() => (pending = null));
      return pending;
    },
  };
})();

// Sends a standard event (https://shopify.dev/docs/api/storefront-events-and-actions) from `target`: `Kind` is its
// class in window.StandardEvents. Returns { resolve, reject } for its promise, or null where the page has no such
// event. The promise the event hands its listeners is observed, so a failed request is not an unhandled rejection.
function standardEvent(Kind, detail, target) {
  if (typeof Kind?.createPromise !== 'function') return null;
  const deferred = Kind.createPromise();
  const event = new Kind({ ...detail, promise: deferred.promise });
  event.promise?.catch(() => {});
  target.dispatchEvent(event);
  return deferred;
}

function dispatchCartError(target, message, code) {
  const { CartErrorEvent } = window.StandardEvents || {};
  if (CartErrorEvent) target.dispatchEvent(new CartErrorEvent({ error: message, code }));
}

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    // The markup has no role or expanded state on a summary: without this script a summary is a plain disclosure, and
    // the state is written here as it connects and each time its details open or close. toggle does not bubble, so it
    // is caught on its way down.
    this.querySelectorAll('summary').forEach((summary) => {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(summary.parentElement.open));
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });
    this.addEventListener(
      'toggle',
      (event) => {
        const summary = event.target.querySelector?.(':scope > summary');
        summary?.setAttribute('aria-expanded', String(event.target.open));
      },
      { capture: true }
    );
    // A button closes the level it sits in, except the country and language control, whose drawer opens over this one.
    this.querySelectorAll('button:not(.dropdown-trigger)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    // The level that shows: a closing submenu keeps `open` while it slides away, so a quick second Escape
    // closes the drawer instead of that submenu again.
    const openDetailsElement = event.target.closest('details.menu-opening');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(summaryElement) : this.openMenuDrawer(summaryElement);

      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        // The panel is hidden until its slide starts, so focus waits for the slide to end. Reduced motion does not skip
        // it: the theme's one switch shortens the slide to 0.01 ms (assets/interaction.css), which still ends.
        const slides = parseFloat(getComputedStyle(summaryElement.nextElementSibling).transitionDuration) > 0;
        slides ? summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus) : addTrapFocus();
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset = this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(elementToFocus) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', false);
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    elementToFocus.setAttribute('aria-expanded', false);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    // The panel slides out in one --motion-time-long, and `open` stays until it has.
    const slideTime = motionTokens({ long: true }).duration;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < slideTime) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        // The level above takes the focus back only while it shows. A drawer that is closing has dropped menu-opening
        // and put the focus on its button already, so a submenu that finishes sliding inside it moves nothing.
        const level = detailsElement.closest('details[open]');
        if (level?.classList.contains('menu-opening')) trapFocus(level, detailsElement.querySelector('summary'));
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

/*
  The clear button of a field that filters what is already in the page (snippets/field.liquid, end 'filter'): it
  empties the field, tells whoever listens with an input event, and keeps the focus in the field. The pointer going down
  on it must not move the focus off the field (Safari gives a button no focus on a click, so the field would blur
  first and a panel that closes on blur would close under the pointer).
*/
document.addEventListener('mousedown', (event) => {
  if (event.target.closest?.('[data-field-clear]')) event.preventDefault();
});

document.addEventListener('click', (event) => {
  const input = event.target.closest?.('[data-field-clear]')?.closest('.field')?.querySelector('.field__input');
  if (!input) return;
  input.value = '';
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();
});
